import {
  useV2CreateApprovalLevelMutation,
  useV2UpdateApprovalLevelMutation,
  useV2DeleteApprovalLevelMutation,
  useV2ChangeApprovalLevelPositionByIdMutation,
  useV2RestoreApprovalLevelByIdMutation,
  useV2PurgeApprovalLevelByIdMutation,
  useV2ConsolidateTechnicalApprovalLevelsMutation,
} from "@generated";
import type {
  ApprovalLevelApprover,
  ApprovalLevelFormValues,
  ApprovalTrigger,
} from "../types/ApprovalLevel";

const triggerForWire = (t: ApprovalTrigger) => {
  const out: any = {
    measure: t.measure,
    evaluation: t.evaluation,
    comparison_value: t.comparison_value,
  };
  if (t.id != null) out.id = t.id;
  if (t.evaluation === "between" && t.maximum_value != null) {
    out.maximum_value = t.maximum_value;
  }
  return out;
};

const approverForWire = (a: ApprovalLevelApprover, idx: number) => {
  const out: any = {
    user_id: Number(a.userId),
    required: !!a.required,
    lob_ids: a.lobIds.map(Number),
    position: idx + 1,
  };
  const numericId = a.id != null ? Number(a.id) : NaN;
  if (Number.isFinite(numericId) && numericId > 0) out.id = numericId;
  return out;
};

const levelAttributes = (values: ApprovalLevelFormValues) => ({
  name: values.name,
  kind: values.kind,
  "signatures-required": values.signaturesRequired,
  "submitter-may-approve": values.submitterMayApprove,
  "limit-approvals-to-team": values.limitApprovalsToTeam,
  triggers: values.triggers.map(triggerForWire),
  approvers: values.approvers.map(approverForWire),
});

export const useApprovalLevelMutations = () => {
  const [create, createState] = useV2CreateApprovalLevelMutation();
  const [update, updateState] = useV2UpdateApprovalLevelMutation();
  const [destroy, destroyState] = useV2DeleteApprovalLevelMutation();
  const [move, moveState] = useV2ChangeApprovalLevelPositionByIdMutation();
  const [restore, restoreState] = useV2RestoreApprovalLevelByIdMutation();
  const [purge, purgeState] = useV2PurgeApprovalLevelByIdMutation();
  const [consolidate, consolidateState] =
    useV2ConsolidateTechnicalApprovalLevelsMutation();

  return {
    create: (values: ApprovalLevelFormValues) =>
      create({
        body: {
          data: {
            type: "approval-levels",
            attributes: levelAttributes(values),
          },
        } as any,
      }).unwrap(),

    update: (id: string, values: ApprovalLevelFormValues) =>
      update({
        id: id,
        body: {
          data: {
            type: "approval-levels",
            id: id,
            attributes: levelAttributes(values),
          },
        } as any,
      }).unwrap(),

    destroy: (id: string) => destroy({ id: id }).unwrap(),
    restore: (id: string) => restore({ id: id }).unwrap(),
    purge: (id: string) => purge({ id: id }).unwrap(),

    // Flattens all active kind=technical levels into the lowest-position one.
    // Account-level; takes no arguments.
    consolidateTechnical: () => consolidate().unwrap(),

    move: (id: string, position: number) =>
      move({
        id: id,
        body: {
          data: {
            type: "approval-levels",
            id: id,
            attributes: { position },
          },
        } as Parameters<typeof move>[0]["body"],
      }).unwrap(),

    states: {
      createState,
      updateState,
      destroyState,
      moveState,
      restoreState,
      purgeState,
      consolidateState,
    },
  };
};
